import React, { useState } from "react";
import { Utensils, Check, X, Loader2, LogOut } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useMealApprovalDetails, useApproveMeal, useDenyMeal } from "../../hooks/useFamily";
import { useGluceraAuth } from "../../hooks/useGluceraAuth";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

interface MealApprovalModalProps {
  approvalToken: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const MealApprovalModal: React.FC<MealApprovalModalProps> = ({
  approvalToken,
  isOpen,
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { logout, isAuthenticated } = useGluceraAuth();
  const navigate = useNavigate();

  // Fetch meal approval details only when modal is open and user is signed in
  const {
    data: mealDetails,
    isLoading: loadingDetails,
  } = useMealApprovalDetails(approvalToken || "", {
    enabled: isOpen && Boolean(approvalToken) && isAuthenticated,
  });

  // Mutations
  const approveMealMutation = useApproveMeal();
  const denyMealMutation = useDenyMeal();

  const handleLogout = () => {
    logout();
    onClose();
  };

  const handleApprove = async () => {
    if (!approvalToken) return;

    setIsProcessing(true);
    try {
      await approveMealMutation.mutateAsync(approvalToken);
      
      // Close modal and redirect to home page
      setTimeout(() => {
        onClose();
        navigate(`/glucera${GluceraNavigationRoutes.HOMEPAGE}`, { replace: true });
      }, 1500);
    } catch (error: any) {
      console.error("Error approving meal:", error);
      setTimeout(() => {
        onClose();
      }, 2000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeny = async () => {
    if (!approvalToken) return;

    setIsProcessing(true);
    try {
      await denyMealMutation.mutateAsync({ token: approvalToken });
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error("Error denying meal:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading state
  if (loadingDetails) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[95%] sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-6 sm:py-8">
            <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-emerald-600 mb-3 sm:mb-4" />
            <p className="text-xs sm:text-sm text-muted-foreground">Loading meal details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Calculate meal time ago
  const getMealTimeAgo = (loggedAt: string) => {
    const now = new Date();
    const mealTime = new Date(loggedAt);
    const diffMs = now.getTime() - mealTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95%] sm:max-w-md p-0 gap-0 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-emerald-600 to-green-600 px-5 py-6 text-center">
          <div className="flex justify-center mb-3">
            <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full">
              <Utensils className="h-6 w-6 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-white mb-1">
            Meal Approval Request
          </h2>
          <p className="text-xs sm:text-sm text-white/90">
            {mealDetails?.logged_by_name} wants to log a meal for you
          </p>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6 space-y-4">
          {/* Meal Info Card */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Meal Type
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {mealDetails?.meal_type || "Meal"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Total Calories
                </p>
                <p className="text-sm font-bold text-emerald-600">
                  {mealDetails?.total_calories || 0} cal
                </p>
              </div>
            </div>

            {/* Food Items */}
            <div className="border-t border-border pt-3">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                Food Items
              </p>
              <div className="space-y-1.5">
                {mealDetails?.food_items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">
                      {item.name}
                      {item.quantity && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({item.quantity})
                        </span>
                      )}
                    </span>
                    <span className="text-muted-foreground font-medium">
                      {item.calories} cal
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* GHI Score if available */}
            {mealDetails?.ghi_score !== undefined && mealDetails?.ghi_score !== null && (
              <div className="border-t border-border pt-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Health Impact Score
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-emerald-600">
                    {mealDetails.ghi_score}/100
                  </span>
                  {mealDetails.ghi_label && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                      {mealDetails.ghi_label}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Logged time */}
            <div className="text-xs text-muted-foreground">
              Logged {getMealTimeAgo(mealDetails?.logged_at || "")}
            </div>
          </div>

          {/* Info message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong className="font-semibold">Note:</strong> Approving will add this meal to your daily log and count towards your nutrition goals.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              onClick={handleDeny}
              disabled={isProcessing}
              variant="outline"
              className="flex-1 h-11 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              {isProcessing && denyMealMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Declining...
                </>
              ) : (
                <>
                  <X className="mr-2 h-4 w-4" />
                  Decline
                </>
              )}
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isProcessing}
              className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700"
            >
              {isProcessing && approveMealMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Approve & Log
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border bg-muted/30 px-5 sm:px-6 py-3">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors mx-auto"
          >
            <LogOut className="h-3 w-3" />
            <span>Not you? Logout</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MealApprovalModal;
